// Team Workspace — Accessible Collaboration (v26).
//
// Square Code-native spaces → channels → threads → messages, with
// reactions, pins, and approval-gated agent drafts. Google-vibe
// accessibility: real buttons everywhere (keyboard focusable), visible
// focus rings, text labels with icons, honest empty states. NO
// Microsoft Teams integration — this is native, internal-only
// messaging; no external message can be sent from this lane.
//
// Presentation: the QUAM-style workspace frame (design language only —
// every route call, guard, and approval gate is unchanged). Channel
// switching became WorkspaceModeTabs (local state, not routes); the
// thread conversation is the document pane; agent drafts render as
// governed agent cards that stay APPROVAL REQUIRED until you decide;
// the composer is the shared action composer with the draft gate
// spelled out. All truth still comes from the team-workspace routes.

import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Banner,
  EmptyState,
  InlineAlert,
  WorkspaceCommandShell,
  WorkspaceModuleCard,
  WorkspaceAgentCard,
  WorkspaceDocumentPane,
  WorkspaceDocSection,
  WorkspaceActionComposer,
  WorkspaceProofPanel,
  WorkspaceRightRail,
  WorkspaceRailSection,
  WorkspaceRailChips,
  WorkspaceStatusChip,
  WORKSPACE_ACCENTS,
  WORKSPACE_FONT,
  WORKSPACE_SURFACE,
  WORKSPACE_TEXT,
  workspaceTint,
} from '@square/ui'
import { api, ApiError, isAuthError } from '../../../api/client'

const ACCENT = WORKSPACE_ACCENTS.cyan

function formatError(err: unknown): string {
  if (err instanceof ApiError) return !err.message || err.message === err.code ? err.code : `${err.code}: ${err.message}`
  if (err instanceof Error) return err.message || 'request_failed'
  return String(err)
}

const inputStyle = {
  padding: '8px 12px', fontSize: 13,
  background: 'rgba(255,255,255,0.03)', color: WORKSPACE_TEXT.primary,
  border: `1px solid ${workspaceTint(ACCENT, 14)}`, borderRadius: 8,
  outlineColor: ACCENT, fontFamily: WORKSPACE_FONT.ui,
} as const

const emptyTextStyle = {
  fontSize: 12, lineHeight: 1.6, color: WORKSPACE_TEXT.tertiary, fontFamily: WORKSPACE_FONT.ui,
} as const

type Space = { id: string; name: string }
type Channel = { id: string; space_id: string; name: string }
type Thread = { id: string; title: string; status: string }
type Message = { id: string; sender_type: string; sender_id: string; body: string; message_kind: string; metadata?: { composer?: string } }

export default function TeamWorkspace() {
  const [spaces, setSpaces] = useState<Space[] | null>(null)
  const [channels, setChannels] = useState<Channel[]>([])
  const [activeChannel, setActiveChannel] = useState<Channel | null>(null)
  const [threads, setThreads] = useState<Thread[]>([])
  const [activeThread, setActiveThread] = useState<Thread | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [pendingDrafts, setPendingDrafts] = useState<Message[]>([])
  const [agents, setAgents] = useState<string[]>([])

  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)
  const [busy, setBusy] = useState(false)
  const [spaceName, setSpaceName] = useState('')
  const [threadTitle, setThreadTitle] = useState('')
  const [composer, setComposer] = useState('')

  const guard = useCallback((err: unknown) => {
    if (isAuthError(err)) { setAuthRequired(true); return }
    setError(formatError(err))
  }, [])

  const refresh = useCallback(async () => {
    try {
      const [s, roster] = await Promise.all([api.teamWorkspace.spaces(), api.commandCenter.agents.list()])
      setSpaces(s.spaces)
      setChannels(s.channels)
      setAgents(roster.agents.filter((a) => a.status !== 'paused').map((a) => a.agent_id))
    } catch (err) { guard(err) }
  }, [guard])

  useEffect(() => { void refresh() }, [refresh])

  const openChannel = useCallback(async (channel: Channel) => {
    setActiveChannel(channel)
    setActiveThread(null)
    try {
      const result = await api.teamWorkspace.threads(channel.id)
      setThreads(result.threads)
    } catch (err) { guard(err) }
  }, [guard])

  const openThread = useCallback(async (thread: Thread) => {
    setActiveThread(thread)
    try {
      const result = await api.teamWorkspace.messages(thread.id)
      setMessages(result.messages as Message[])
      setPendingDrafts(result.pending_drafts as Message[])
      await api.teamWorkspace.messageAction({ action: 'mark_read', thread_id: thread.id })
    } catch (err) { guard(err) }
  }, [guard])

  const run = useCallback(async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try { await fn() } catch (err) { guard(err) } finally { setBusy(false) }
  }, [guard])

  // Channel lanes → mode tabs (local state only; switching a lane loads
  // its threads exactly as the old channel buttons did).
  const spaceNames = new Map((spaces ?? []).map((s) => [s.id, s.name]))
  const channelModes = channels.map((c) => ({
    key: c.id,
    label: (spaces?.length ?? 0) > 1 ? `${spaceNames.get(c.space_id) ?? '?'} / #${c.name}` : `#${c.name}`,
    accent: ACCENT,
    hint: spaceNames.get(c.space_id),
  }))

  // Header chips reflect REAL state: syncing while the spaces route is
  // in flight, error when a request failed, powered + governed only
  // once real rows loaded. INTERNAL ONLY is structural truth.
  const shellStatus = authRequired
    ? 'requires_setup'
    : error
      ? 'error'
      : spaces === null
        ? 'syncing'
        : ['powered', 'governed']

  return (
    <WorkspaceCommandShell
      kicker="SQUARE CODE · TEAM WORKSPACE"
      title="Team Workspace"
      subtitle="Native spaces, channels, threads, and messages — internal only, keyboard-friendly, honest empty states. Agents draft replies you approve before they appear. No Microsoft Teams integration; no external message can be sent from here."
      accent={ACCENT}
      status={shellStatus}
      chips={<WorkspaceStatusChip status="internal_only" />}
      actions={<Button variant="secondary" onClick={() => void refresh()}>Refresh</Button>}
      modes={channelModes}
      activeMode={activeChannel?.id}
      onModeChange={(key: string) => {
        const channel = channels.find((c) => c.id === key)
        if (channel) void openChannel(channel)
      }}
      rail={authRequired ? undefined : (
        <WorkspaceRightRail>
          <WorkspaceRailSection label="LANE GUARANTEES" accent={WORKSPACE_ACCENTS.purple}>
            <WorkspaceProofPanel
              title="HOW THIS LANE WORKS"
              rows={[
                {
                  id: 'dispatch',
                  label: 'Message dispatch',
                  status: 'powered',
                  detail: 'Native Square Code messaging only. No Microsoft Teams integration — no external message can be sent from this lane.',
                },
                {
                  id: 'drafts',
                  label: 'Agent replies',
                  status: 'approval_required',
                  detail: 'Agents draft replies you approve before they appear. A draft never posts on its own.',
                },
                {
                  id: 'read',
                  label: 'Read state',
                  status: 'powered',
                  detail: 'Opening a thread marks it read through the messages route.',
                },
              ]}
              sourceLabel="team-workspace routes · agent registry"
            />
          </WorkspaceRailSection>

          <WorkspaceRailSection label="SPACES" accent={ACCENT} badge={spaces === null ? undefined : String(spaces.length)}>
            {spaces !== null && spaces.length > 0 ? (
              <WorkspaceRailChips items={spaces.map((s) => ({ label: s.name, accent: ACCENT }))} />
            ) : (
              <div style={emptyTextStyle}>
                {spaces === null
                  ? 'Loading from the spaces route…'
                  : 'No spaces yet — create the first one from the Spaces card.'}
              </div>
            )}
          </WorkspaceRailSection>

          <WorkspaceRailSection label="AGENT ROSTER" accent={WORKSPACE_ACCENTS.purple} badge={spaces === null ? undefined : String(agents.length)}>
            {agents.length > 0 ? (
              <WorkspaceRailChips items={agents.map((a) => ({ label: a, accent: WORKSPACE_ACCENTS.purple }))} />
            ) : (
              <div style={emptyTextStyle}>
                {spaces === null
                  ? 'Loading the roster…'
                  : 'No unpaused agents in the registry — activate one in Agent Control to request drafts.'}
              </div>
            )}
            <div style={{ marginTop: 6, fontSize: 9, color: WORKSPACE_TEXT.muted, fontFamily: WORKSPACE_FONT.mono, letterSpacing: '0.06em' }}>
              source: agent registry · paused agents excluded
            </div>
          </WorkspaceRailSection>

          {activeThread && (
            <WorkspaceRailSection label="THIS THREAD" accent={ACCENT}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                <WorkspaceStatusChip status={activeThread.status} size="sm" />
              </div>
              <WorkspaceRailChips
                items={[
                  { label: `${messages.length} messages`, accent: ACCENT },
                  { label: `${pendingDrafts.length} drafts pending`, accent: pendingDrafts.length > 0 ? WORKSPACE_ACCENTS.purple : undefined },
                ]}
              />
            </WorkspaceRailSection>
          )}
        </WorkspaceRightRail>
      )}
      composer={!authRequired && activeThread ? (
        <WorkspaceActionComposer
          accent={ACCENT}
          value={composer}
          onChange={setComposer}
          onSubmit={() => {
            if (!composer.trim() || busy) return
            void run(async () => {
              await api.teamWorkspace.messageAction({ action: 'post', thread_id: activeThread.id, body: composer.trim() })
              setComposer('')
              await openThread(activeThread)
            })
          }}
          placeholder="Write a message… (Enter to send — internal only)"
          submitLabel="Send"
          busy={busy}
          busyLabel="Sending"
          attachments={agents.length > 0 ? [{
            key: 'agent-draft',
            glyph: '✎',
            label: `Ask ${agents[0]} to draft`,
            disabled: busy || !composer.trim(),
            hint: 'Your text becomes the instruction — the agent reply arrives as a draft for your approval.',
            onClick: () => {
              if (!composer.trim() || busy) return
              void run(async () => {
                await api.teamWorkspace.requestDraft(activeThread.id, agents[0], composer.trim())
                setComposer('')
                await openThread(activeThread)
              })
            },
          }] : []}
          gateLabel={agents.length > 0 ? 'Agent drafts are approval required — nothing an agent writes appears until you approve it.' : undefined}
          hint="internal only — no external message leaves this lane"
        />
      ) : undefined}
    >
      {error && <div style={{ marginBottom: 14 }}><Banner severity="warning" title="Team Workspace">{error}</Banner></div>}

      {authRequired ? (
        <EmptyState message="Sign in to continue." />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 16 }}>
            {/* Spaces — capability card + create form */}
            <WorkspaceModuleCard
              glyph="▦"
              title="Spaces"
              accent={ACCENT}
              status={spaces === null ? 'syncing' : 'powered'}
              summary="Native Square Code spaces. Channel lanes appear as tabs above — pick one to work its threads."
              meta={spaces !== null ? [
                { label: 'spaces', value: spaces.length },
                { label: 'channels', value: channels.length },
              ] : undefined}
              footnote="new spaces start with a #general channel"
            >
              <div style={{ display: 'flex', gap: 6 }}>
                <input aria-label="New space name" value={spaceName} onChange={(e) => setSpaceName(e.target.value)} placeholder="New space…" style={{ ...inputStyle, flex: 1, minWidth: 0 }} />
                <Button loading={busy} disabled={busy || !spaceName.trim()} onClick={() => void run(async () => {
                  await api.teamWorkspace.createSpace(spaceName.trim())
                  setSpaceName('')
                  await refresh()
                })}>Create</Button>
              </div>
              {spaces === null && <InlineAlert severity="info">Loading spaces…</InlineAlert>}
              {spaces !== null && spaces.length === 0 && (
                <div style={emptyTextStyle}>No spaces yet — create one; it starts with a #general channel.</div>
              )}
              {spaces !== null && spaces.length > 0 && (
                <div style={{ display: 'grid', gap: 6 }}>
                  {spaces.map((s) => (
                    <div key={s.id} style={{ padding: '8px 10px', borderRadius: 8, background: WORKSPACE_SURFACE.card, border: `1px solid ${WORKSPACE_SURFACE.lineFaint}` }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: WORKSPACE_TEXT.primary, fontFamily: WORKSPACE_FONT.ui, marginBottom: 4 }}>{s.name}</div>
                      <WorkspaceRailChips
                        items={channels.filter((c) => c.space_id === s.id).map((c) => ({
                          label: `#${c.name}`,
                          accent: activeChannel?.id === c.id ? ACCENT : undefined,
                          hint: 'switch lanes in the tabs above',
                        }))}
                      />
                    </div>
                  ))}
                </div>
              )}
            </WorkspaceModuleCard>

            {/* Threads — capability card + create form */}
            <WorkspaceModuleCard
              glyph="≡"
              title="Threads"
              accent={ACCENT}
              status={activeChannel ? 'powered' : undefined}
              summary={activeChannel ? `Threads in #${activeChannel.name}.` : 'Threads live inside a channel lane.'}
              footnote={activeChannel ? `#${activeChannel.name}` : 'no lane selected'}
            >
              {!activeChannel ? (
                <div style={emptyTextStyle}>Pick a channel lane above to see its threads.</div>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input aria-label="New thread title" value={threadTitle} onChange={(e) => setThreadTitle(e.target.value)} placeholder="New thread…" style={{ ...inputStyle, flex: 1, minWidth: 0 }} />
                    <Button loading={busy} disabled={busy || !threadTitle.trim()} onClick={() => void run(async () => {
                      await api.teamWorkspace.createThread(activeChannel.id, threadTitle.trim())
                      setThreadTitle('')
                      await openChannel(activeChannel)
                    })}>Open</Button>
                  </div>
                  {threads.length === 0 && <div style={emptyTextStyle}>No threads yet in #{activeChannel.name} — open the first one above.</div>}
                  {threads.length > 0 && (
                    <div style={{ display: 'grid', gap: 4 }}>
                      {threads.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => void openThread(t)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                            padding: '9px 11px', borderRadius: 8,
                            border: `1px solid ${activeThread?.id === t.id ? workspaceTint(ACCENT, 30) : WORKSPACE_SURFACE.line}`,
                            background: activeThread?.id === t.id ? workspaceTint(ACCENT, 10) : WORKSPACE_SURFACE.card,
                            color: WORKSPACE_TEXT.primary, cursor: 'pointer',
                            fontSize: 12.5, fontWeight: 600, fontFamily: WORKSPACE_FONT.ui,
                            outlineColor: ACCENT,
                          }}
                        >
                          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
                          <WorkspaceStatusChip status={t.status} size="sm" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </WorkspaceModuleCard>
          </div>

          {/* Thread conversation — the document/work pane */}
          <WorkspaceDocumentPane
            accent={ACCENT}
            title={activeThread ? activeThread.title : undefined}
            meta={activeThread ? `#${activeChannel?.name ?? 'channel'} · internal only` : undefined}
            maxWidth={720}
            empty={{
              glyph: '◈',
              title: 'Open a thread to read and reply',
              body: 'Agents can draft replies for your approval — drafts never appear until you approve them.',
            }}
          >
            {activeThread && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <WorkspaceStatusChip status={activeThread.status} size="sm" />
                </div>
                <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                  {messages.length === 0 && <div style={emptyTextStyle}>No messages yet — say something below.</div>}
                  {messages.map((m) => (
                    <WorkspaceDocSection
                      key={m.id}
                      agent={{
                        name: m.sender_type === 'human'
                          ? 'you'
                          : `${m.sender_id}${m.sender_type === 'agent' && m.metadata?.composer ? ` · ${m.metadata.composer === 'llm' ? 'Claude-composed' : 'scripted'} · approved` : ''}`,
                        accent: m.sender_type === 'human' ? ACCENT : WORKSPACE_ACCENTS.purple,
                      }}
                    >
                      {m.body}
                    </WorkspaceDocSection>
                  ))}
                </div>

                {pendingDrafts.length > 0 && (
                  <WorkspaceDocSection kind="proof" heading="Agent drafts awaiting your approval" accent={WORKSPACE_ACCENTS.purple}>
                    <div style={{ display: 'grid', gap: 8 }}>
                      {pendingDrafts.map((d) => (
                        <WorkspaceAgentCard
                          key={d.id}
                          compact
                          name={d.sender_id}
                          role="drafted reply"
                          accent={WORKSPACE_ACCENTS.purple}
                          state="approval_required"
                          summary={d.body}
                          evidence="held until you decide — a draft never posts on its own"
                          actions={
                            <>
                              <Button variant="secondary" disabled={busy} onClick={() => void run(async () => {
                                await api.teamWorkspace.decideDraft(d.id, 'approved')
                                await openThread(activeThread)
                              })}>Approve</Button>
                              <Button variant="secondary" disabled={busy} onClick={() => void run(async () => {
                                await api.teamWorkspace.decideDraft(d.id, 'rejected')
                                await openThread(activeThread)
                              })}>Reject</Button>
                            </>
                          }
                        />
                      ))}
                    </div>
                  </WorkspaceDocSection>
                )}
              </>
            )}
          </WorkspaceDocumentPane>
        </>
      )}
    </WorkspaceCommandShell>
  )
}
